import { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/apiService';

/**
 * Data-fetching and mutation logic for the Devices admin page.
 *
 * Owns the device list (merged with live online/offline status), the
 * expanded-device revenue analytics (details/daily chart data/player spend),
 * and the mutation handlers (register, delete, toggle active, reconcile,
 * edit settings, fetch logs/reconciliation history). All mutation helpers
 * throw on failure so the page can decide how to surface the error (toast
 * text differs per action in the original component).
 */
export function useDevices() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [deviceDetails, setDeviceDetails] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [playerSpend, setPlayerSpend] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const loadDevices = useCallback(async () => {
    try {
      setLoading(true);
      const [revenueResponse, statusResponse] = await Promise.all([
        apiService.getDeviceRevenue(),
        apiService.getDeviceStatus().catch(() => ({ data: [] }))
      ]);

      const statusById = new Map(
        (statusResponse.data || []).map((status) => [status.id, status])
      );

      const mergedDevices = (revenueResponse.data || []).map((device) => {
        const status = statusById.get(device.id);
        return {
          ...device,
          isOnline: status ? status.isOnline : false,
          local_ip: status ? status.local_ip : null,
          remote_ip: status ? status.remote_ip : null,
          connection: status ? status.connection : null
        };
      });

      setDevices(mergedDevices);
    } catch (err) {
      setError('Failed to load devices');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  const loadDeviceDetails = useCallback(async (deviceId, dateRange) => {
    try {
      setLoadingDetails(true);
      const [details, allDaily, spend] = await Promise.all([
        apiService.getDeviceRevenueDetails(deviceId),
        apiService.getDeviceRevenueDaily(deviceId, { days: 365 }), // Fetch all data
        apiService.getDevicePlayerSpend(deviceId)
      ]);

      setDeviceDetails(details.data);

      // Calculate expected revenue progression for burndown chart
      const detailsData = details.data;
      const revenueGoal = parseFloat(detailsData.revenue_goal || 1500);
      const dailyTarget = revenueGoal / 90; // 90-day goal target

      // Get the start date (revenue tracking start date or device created date)
      const startDate = new Date(detailsData.revenue_tracking_start_date || detailsData.device_activated);
      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);
      startDate.setHours(0, 0, 0, 0);

      // Calculate the end date for the chart based on selected range
      const chartEndDate = new Date(currentDate);
      const chartStartDate = new Date(currentDate);
      chartStartDate.setDate(chartStartDate.getDate() - dateRange + 1);

      // Make sure we don't go before the actual start date
      if (chartStartDate < startDate) {
        chartStartDate.setTime(startDate.getTime());
      }

      // Create a map of ALL daily data for quick lookup
      const dailyDataMap = new Map();
      const dailyDataArray = Array.isArray(allDaily.data) ? allDaily.data : [];

      dailyDataArray.forEach(day => {
        const revenue = parseFloat(day.total_revenue || 0);
        // Normalize the date to YYYY-MM-DD format
        const normalizedDate = day.date.split('T')[0];
        dailyDataMap.set(normalizedDate, revenue);
      });

      // Calculate cumulative revenue from the start date up to chart start date
      let cumulativeActual = 0;
      const iterDate = new Date(startDate);
      while (iterDate < chartStartDate) {
        const dateStr = iterDate.toISOString().split('T')[0];
        cumulativeActual += dailyDataMap.get(dateStr) || 0;
        iterDate.setDate(iterDate.getDate() + 1);
      }

      // Generate complete daily data for the chart date range
      const enrichedDaily = [];
      iterDate.setTime(chartStartDate.getTime()); // Reset to chart start

      while (iterDate <= chartEndDate) {
        const dateStr = iterDate.toISOString().split('T')[0];

        // Get the day number from the start date (for expected revenue calculation)
        // Expected revenue should only start accumulating from the tracking start date
        let expectedRevenue = 0;
        if (iterDate >= startDate) {
          const daysSinceStart = Math.floor((iterDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
          expectedRevenue = dailyTarget * daysSinceStart;
        }

        // Get actual revenue for this day (if it exists)
        const dayRevenue = dailyDataMap.get(dateStr) || 0;
        cumulativeActual += dayRevenue;

        enrichedDaily.push({
          date: dateStr,
          total_revenue: dayRevenue,
          expected_revenue: expectedRevenue,
          cumulative_actual: cumulativeActual
        });

        iterDate.setDate(iterDate.getDate() + 1);
      }

      setDailyData(enrichedDaily);
      setPlayerSpend(spend.data);
    } catch (err) {
      console.error('Failed to load device details:', err);
    } finally {
      setLoadingDetails(false);
    }
  }, []);

  const clearDeviceDetails = useCallback(() => {
    setDeviceDetails(null);
    setDailyData([]);
    setPlayerSpend(null);
  }, []);

  const registerDevice = useCallback(async (newDevice) => {
    const response = await apiService.registerDevice(newDevice);
    await loadDevices();
    return response.data.api_key;
  }, [loadDevices]);

  const fetchDeviceRecord = useCallback(async (deviceId) => {
    const response = await apiService.getDeviceDetails(deviceId);
    return response.data;
  }, []);

  const deleteDevice = useCallback(async (deviceId, confirmationText) => {
    await apiService.deleteDevice(deviceId, confirmationText);
    await loadDevices();
  }, [loadDevices]);

  const toggleDeviceActive = useCallback(async (device) => {
    await apiService.updateDevice(device.id, { isActive: !device.is_active });
    await loadDevices();
  }, [loadDevices]);

  const fetchDeviceLogs = useCallback(async (deviceId) => {
    const response = await apiService.getDeviceLogs(deviceId, { limit: 50 });
    return response.data.logs || [];
  }, []);

  const fetchDeviceReconciliations = useCallback(async (deviceId) => {
    const response = await apiService.getDeviceReconciliations(deviceId);
    return response.data.reconciliations || [];
  }, []);

  const reconcileDevice = useCallback(async (deviceId, data) => {
    const response = await apiService.reconcileDevice(deviceId, data);
    await loadDevices();
    return response.data;
  }, [loadDevices]);

  const updateDeviceSettings = useCallback(async (deviceId, data) => {
    await apiService.updateDevice(deviceId, data);
    await loadDevices();
  }, [loadDevices]);

  return {
    devices,
    loading,
    error,
    loadDevices,
    deviceDetails,
    dailyData,
    playerSpend,
    loadingDetails,
    loadDeviceDetails,
    clearDeviceDetails,
    registerDevice,
    fetchDeviceRecord,
    deleteDevice,
    toggleDeviceActive,
    fetchDeviceLogs,
    fetchDeviceReconciliations,
    reconcileDevice,
    updateDeviceSettings
  };
}

export default useDevices;
